import { supportsPushNotifications } from "../../utils/browserCapabilities";
import type { PushNotificationDevicesGetResponseType } from "@sellar-io/api-v2/types";
import { InfiniteData, UseInfiniteQueryResult } from "@tanstack/react-query";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { getMessaging, isSupported } from "firebase/messaging";
import { firebaseApp, getFirebaseMessagingToken } from "../../firebaseApp";
import DeviceDetector from "device-detector-js";

export type PushNotificationStoreState = {
  settingsActive: boolean;
  guideActive: boolean;
};

export type PushNotificationStoreActions = {
  setSettingsActive: (active: boolean) => void;
  setGuideActive: (active: boolean) => void;
};

const initialPushNotificationStoreState: PushNotificationStoreState = {
  settingsActive: false,
  guideActive: false,
};

export const usePushNotificationStore = create<
  PushNotificationStoreState & PushNotificationStoreActions
>()(
  persist(
    (set) => ({
      ...initialPushNotificationStoreState,
      setSettingsActive: (active) =>
        set(() => ({
          settingsActive: active,
        })),
      setGuideActive: (active) =>
        set(() => ({
          guideActive: active,
        })),
    }),
    {
      name: "pushNotifications",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        settingsActive: state.settingsActive,
        guideActive: state.guideActive,
      }),
    },
  ),
);

const promptNotificationPermission = async (): Promise<boolean> => {
  // Check if Notification API is available
  if (!("Notification" in window)) {
    return false;
  }
  if (Notification.permission === "granted") return true;
  if (Notification.permission === "default") {
    return await Notification.requestPermission().then(
      (permission) => permission === "granted",
    );
  }
  return false;
};

export const getIsPushNotificationsDisabled = ({
  pushNotificationDevicesQuery,
}: {
  pushNotificationDevicesQuery: UseInfiniteQueryResult<
    InfiniteData<PushNotificationDevicesGetResponseType>,
    Error
  >;
}) => {
  const notificationNotGranted = () => {
    if (!supportsPushNotifications()) return true;
    return Notification.permission !== "granted";
  };
  const deviceId = getDeviceId();
  const existingDevice = pushNotificationDevicesQuery.data?.pages.flatMap(
    (page) => page.filter((device) => device.deviceId === deviceId),
  );

  const deviceDisabled = !existingDevice || existingDevice.length == 0;

  const pushNotificationsDisabled = notificationNotGranted() || deviceDisabled;

  return {
    pushNotificationsDisabled,
    deviceDisabled,
  };
};

export const getDeviceId = (): string => {
  const deviceDetector = new DeviceDetector();
  const device = deviceDetector.parse(navigator.userAgent);

  const brand = device.device?.brand ?? "unknownBrand";
  const type = device.device?.type ?? "unknownType";
  const os = device.os?.name ?? "unknownOS";
  const browser = device.client?.name ?? "unknownBrowser";
  return `${brand}-${type}-${os}-${browser}`;
};

export const generateAndSetFirebaseMessagingToken = async (): Promise<
  string | undefined
> => {
  if (!supportsPushNotifications()) return undefined;
  try {
    const permissionGranted = await promptNotificationPermission();
    if (!permissionGranted || !(await isSupported())) return undefined;
    const firebaseMessaging = getMessaging(firebaseApp);
    return await getFirebaseMessagingToken(firebaseMessaging);
  } catch {
    // Let the caller show notification setup guidance if browser policy blocks push.
    return undefined;
  }
};
